import { withTranslation } from '@application/i18n/with_translation';
import { Box, BreakPoints, Divider, FileViewer, Flex, Icon, IconProps, Spacer, Stack, Text, useBreakpoint } from '@cellula/react';
import { primitiveColorTokens, tokens } from '@cellula/react-theme-patient';
import { Shimmer } from '@component/new_shimmer.component';
import type { AppointmentData, MetaData } from '@feature/cases/case_appointment_model';
import { FormCard } from '@feature/dynamic_form/form_card.component';
import { AttachedFile } from '@feature/file_manager/attached_file.component';
import { Fragment, ReactNode } from 'react';
import styled from 'styled-components';

// TODO: Needs other data structures
type MergedData = AppointmentData & MetaData;

interface Props {
	data: MergedData | null;
}

const elements = {
	hideWhenDesktop: styled.div`
		display: contents;

		${tokens.breakpointQuery.small} {
			display: none;
		}
	`,
	physicalLocationImage: styled.div`
		width: 100%;
		min-height: 192px;
		overflow: hidden;
		background: ${primitiveColorTokens.neutral[200]};
		border-radius: ${tokens.borderRadius.medium};
		display: flex;
		justify-content: center;
		align-items: center;

		img {
			width: 100%;
		}
	`,
	information: styled.div`
		width: 100%;
		border: 1px solid ${tokens.color.border.default};
		gap: ${tokens.spacing.x1};
		display: flex;
		padding: ${tokens.spacing.x1_5};
		border-radius: ${tokens.borderRadius.medium};
	`,
	receiptLink: styled.a`
		color: ${tokens.color.bg.interactive};
	`,
};

export function InfoViewAppointmentDetails({ data }: Props): JSX.Element | null {
	const _mobileBreakpoint = useBreakpoint(BreakPoints.Small);

	return (
		<Flex
			fluid='horizontal'
			gap={tokens.spacing.x2_5}
			breakpointMediumAndUp={{
				direction: 'row',
			}}
		>
			<Flex
				grow={1}
				shrink={0}
				basis='unset'
				role='group'
				aria-describedby='appointmentInfo'
				breakpointSmallAndUp={{
					basis: '50%',
				}}
			>
				<LeftSection data={data} />
			</Flex>

			<elements.hideWhenDesktop>
				<Divider decorative />
			</elements.hideWhenDesktop>

			<Flex
				grow={1}
				basis='unset'
				breakpointSmallAndUp={{
					basis: '50%',
				}}
			>
				<RightSection data={data} />
			</Flex>
		</Flex>
	);
}

function shouldShowPayment(data: MergedData | null): [shouldShowPaymentSection: boolean, isPayed: boolean] {
	if (data == null || data.payment == null || data.payment.State === 'Expired') return [false, false];

	if (data.reservedUntil != null) {
		return [true, false];
	}

	return [true, data.payment?.State === 'Paid'];
}

function LeftSection({ data }: { data: MergedData | null }): JSX.Element | null {
	const isPhysical = data?.type === 'physical';
	const [showPaymentSection, isPayed] = shouldShowPayment(data);

	const renderPaymentSection = (appointmentData: MergedData) => {
		if (!showPaymentSection) {
			return null;
		}
		if (isPayed) {
			return (
				<Flex asChild>
					<Stack direction='horizontal' gap={tokens.spacing.x1}>
						<Row icon='wallet'>{appointmentData.price?.Formatted ?? ''}</Row>
						{appointmentData.payment?.ReceiptUrl != null ? (
							<elements.receiptLink href={appointmentData.payment.ReceiptUrl} target='_blank' rel='noreferrer'>
								{withTranslation('PW.Component.Visits.AppointmentInfo.Payment.ShowReceipt')}
							</elements.receiptLink>
						) : null}
					</Stack>
				</Flex>
			);
		}
		return (
			<Row icon='wallet' color={tokens.color.danger.contentMuted}>
				{withTranslation('PW.Component.Visits.AppointmentInfo.Payment.NotPaid', {
					value: appointmentData.price?.Formatted ?? '',
				})}
			</Row>
		);
	};

	// TODO: Research how we can do skeleton loading better
	const content =
		data != null ? (
			<Fragment>
				<Text id='appointmentInfo' variant='bodySemiBold'>
					{withTranslation('PW.Component.Visits.AppointmentInfo.Title.AppointmentInformation')}
				</Text>
				{data.representative != null ? (
					<Stack gap={tokens.spacing.x1} direction='horizontal'>
						<Text
							variant='labelSemiBold'
							style={{
								color: tokens.color.content.default,
							}}
						>
							{withTranslation('PW.Component.Visits.AppointmentInfo.RepresentativeFor')}
						</Text>
						<Text
							variant='labelRegular'
							style={{
								color: tokens.color.content.default,
							}}
						>
							{data.representative.name || data.representative.for}
						</Text>
					</Stack>
				) : null}

				<Row icon='calendar'>{data.day}</Row>
				<Row icon='clock'>{data.time}</Row>
				{data.type === 'video' ? <Row icon='video'>{withTranslation('PW.Component.Visits.AppointmentInfo.Video')}</Row> : null}
				<Row icon='house'>{data.receptionName}</Row>

				{renderPaymentSection(data)}

				{isPhysical && data.physicalLocation ? (
					<Row icon='pinMark' topLeading={true}>
						<Box gap={tokens.spacing.x1}>
							<div>
								{data.physicalLocation.locationName}
								<br />
								{data.physicalLocation.address}
								<br />
								{data.physicalLocation.postalCode}
								<br />
								{data.physicalLocation.city}
							</div>

							{data.physicalLocation.imageUrl ? (
								<elements.physicalLocationImage aria-hidden>
									<img src={data.physicalLocation.imageUrl} alt='' />
								</elements.physicalLocationImage>
							) : null}

							{data.physicalLocation.locationDescription ? (
								<elements.information>
									<Icon type='infoCircle' />
									<Text variant='bodySmallRegular'>{data.physicalLocation.locationDescription}</Text>
								</elements.information>
							) : null}
						</Box>
					</Row>
				) : null}
			</Fragment>
		) : (
			<Fragment>
				<Shimmer height={16} width={200} />
				<Stack gap={tokens.spacing.x1} direction='horizontal'>
					<Shimmer height={24} width={24} cornerRadius={24} />
					<Shimmer height={16} width={100} />
				</Stack>
				<Stack gap={tokens.spacing.x1} direction='horizontal'>
					<Shimmer height={24} width={24} cornerRadius={24} />
					<Shimmer height={16} width={100} />
				</Stack>
				<Stack gap={tokens.spacing.x1} direction='horizontal'>
					<Shimmer height={24} width={24} cornerRadius={24} />
					<Shimmer height={16} width={100} />
				</Stack>
				<Stack gap={tokens.spacing.x1} direction='horizontal'>
					<Shimmer height={24} width={24} cornerRadius={24} />
					<Shimmer height={16} width={100} />
				</Stack>
			</Fragment>
		);

	return <Stack gap={tokens.spacing.x1_5}>{content}</Stack>;
}

function RightSection({ data }: { data: MergedData | null }): JSX.Element | null {
	const forms =
		data?.forms
			.filterForms({
				completed: true,
			})
			.map((formWorker) => <FormCard analyticAction='Opened answered form' key={formWorker.formId} formWorker={formWorker} />) ?? [];

	const files = data?.files.map((fileWorker) => <AttachedFile key={fileWorker.id} worker={fileWorker} />) ?? [];

	// TODO: Research how we can do skeleton loading better
	const content =
		data != null ? (
			<Fragment>
				{data.rfv ? (
					<Flex>
						<Text variant='bodySemiBold'>{withTranslation('PW.Component.Visits.AppointmentInfo.Title.ReasonForVisit')}</Text>
						<Text variant='bodyRegular'>{data.rfv}</Text>
					</Flex>
				) : null}

				<Flex>
					<Text variant='bodySemiBold'>{withTranslation('PW.Component.Visits.AppointmentInfo.Title.CaseDescription')}</Text>
					<Text variant='bodyRegular'>{data.reason}</Text>
				</Flex>

				{files.length > 0 ? (
					<FileViewer.Root>
						<Stack gap={tokens.spacing.x1}>
							<Text variant='bodySemiBold'>{withTranslation('PW.Component.Visits.AppointmentInfo.Title.AttachedFiles')}</Text>
							{files}
						</Stack>
					</FileViewer.Root>
				) : null}

				{forms.length > 0 ? (
					<Stack gap={tokens.spacing.x1}>
						<Text variant='bodySemiBold'>{withTranslation('PW.Component.Visits.AppointmentInfo.Title.SentForms')}</Text>
						{forms}
					</Stack>
				) : null}
			</Fragment>
		) : (
			<Fragment>
				<Stack gap='8px'>
					<Shimmer height={16} width={200} />
					<Shimmer height={16} width={90} />
				</Stack>
				<Stack gap='8px'>
					<Shimmer height={16} width={200} />
					<Shimmer height={16} width={150} />
				</Stack>
				<Stack gap='8px'>
					<Shimmer height={16} width={100} />
				</Stack>
				<Stack gap='8px'>
					<Shimmer height={16} width={80} />
				</Stack>
			</Fragment>
		);

	return <Stack gap={tokens.spacing.x2}>{content}</Stack>;
}

interface RowProps {
	icon: IconProps['type'];
	children: ReactNode;
	topLeading?: boolean;
	color?: string;
}

function Row({ icon, children, topLeading, color }: RowProps): JSX.Element {
	return (
		<Text variant='labelRegular' color={color ?? tokens.color.content.default}>
			<Flex align={topLeading ? 'flex-start' : 'center'} direction='row'>
				<Icon type={icon} />
				<Spacer horizontal={tokens.spacing.x1} />
				{children}
			</Flex>
		</Text>
	);
}
